import { MilestoneTracker } from './videoSignal'
import type { VideoSignalHandlers, VimeoTimeUpdate } from './videoSignal'

const YOUTUBE_API_SCRIPT_ID = 'youtube-iframe-api'
const POSITION_POLL_MS = 1_000

export const YOUTUBE_PLAYER_STATE = {
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2
} as const

export interface YouTubePlayerLike {
  getCurrentTime(): number
  getDuration(): number
  destroy(): void
}

interface YouTubePlayerConstructor {
  new(element: HTMLIFrameElement, options: {
    events: {
      onReady: (event: { target: YouTubePlayerLike }) => void
      onStateChange: (event: { data: number }) => void
    }
  }): YouTubePlayerLike
}

interface YouTubeNamespace {
  Player: YouTubePlayerConstructor
}

declare global {
  interface Window {
    YT?: YouTubeNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

let apiPromise: Promise<YouTubeNamespace> | null = null

export function loadYouTubeIframeApi(): Promise<YouTubeNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (apiPromise) return apiPromise

  apiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      try {
        previousReady?.()
      } finally {
        if (window.YT?.Player) resolve(window.YT)
      }
    }

    let script = document.getElementById(YOUTUBE_API_SCRIPT_ID) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = YOUTUBE_API_SCRIPT_ID
      script.src = 'https://www.youtube.com/iframe_api'
      document.head.appendChild(script)
    }
    script.addEventListener('error', () => {
      apiPromise = null
      reject(new Error('YouTube IFrame API failed to load'))
    }, { once: true })
  })

  return apiPromise
}

export async function createYouTubePlayer(
  iframe: HTMLIFrameElement,
  onStateChange: (event: { data: number }) => void
): Promise<YouTubePlayerLike> {
  const youtube = await loadYouTubeIframeApi()
  return new Promise((resolve) => {
    new youtube.Player(iframe, {
      events: {
        onReady: (event) => resolve(event.target),
        onStateChange
      }
    })
  })
}

export class YouTubePlayerSignal {
  private readonly tracker = new MilestoneTracker()
  private timer: number | null = null

  constructor(
    private readonly player: YouTubePlayerLike,
    private readonly handlers: VideoSignalHandlers
  ) {}

  stateChanged(state: number): void {
    if (state === YOUTUBE_PLAYER_STATE.PLAYING) {
      this.handlers.onPlay()
      this.sample()
      this.startPolling()
      return
    }

    this.stopPolling()
    if (state === YOUTUBE_PLAYER_STATE.PAUSED) this.handlers.onPause(this.position())
    if (state === YOUTUBE_PLAYER_STATE.ENDED) this.handlers.onEnded?.(this.position())
    if (![YOUTUBE_PLAYER_STATE.PAUSED, YOUTUBE_PLAYER_STATE.ENDED].includes(state)) {
      const position = this.position()
      if (position) this.handlers.onSeeked?.(position)
    }
  }

  dispose(): void {
    this.stopPolling()
  }

  private startPolling(): void {
    if (this.timer !== null) return
    this.timer = window.setInterval(() => this.sample(), POSITION_POLL_MS)
  }

  private stopPolling(): void {
    if (this.timer === null) return
    window.clearInterval(this.timer)
    this.timer = null
  }

  private sample(): void {
    const position = this.position()
    if (!position) return

    this.tracker.update(position.seconds / position.duration)
      .forEach((milestone) => this.handlers.onMilestone(milestone))
    this.handlers.onTimeUpdate?.(position)
  }

  private position(): VimeoTimeUpdate | undefined {
    const seconds = this.player.getCurrentTime()
    const duration = this.player.getDuration()
    if (!Number.isFinite(seconds) || !Number.isFinite(duration) || duration <= 0) return undefined

    return { seconds, duration, percent: seconds / duration }
  }
}
